<template>
  <div class="integrapay-payment-form">
    <loading-spinner v-show="preparing">
      <div slot="title">
        Loading Transaction Details
      </div>
      <div slot="message">
        Please wait while we retrieve the details of the transaction.
      </div>
    </loading-spinner>
    <loading-spinner
      v-show="processing"
      v-if="displayProcessessing"
    >
      <div slot="title">
        Processing payment
      </div>
      <div slot="message">
        <p>Please wait while we process your credit card payment.</p>
        <p>Please do not reload or close the page until the payment is completed.</p>
      </div>
    </loading-spinner>
    <transaction-error
      v-if="transctionError"
      :error="transctionError"
    />
    <payment-failed
      v-if="paymentFailed"
      :receipt="receiptDetails"
    />
    <payment-complete
      v-if="paymentComplete"
      :receipt="receiptDetails"
    />
    <payment-error
      v-if="paymentException"
      :error="errorDetails"
      :txn="txn"
    />
    <creditCardForm
      v-show="!preparing && newCardOnly && !processing"
      v-model="newCard"
      :payment-testing="paymentTesting"
      :show-card-save="showCardSave"
      @cardValid="cardValidState"
      @hasErrors="setHasdDetailsErrors"
      @override-amount="overrideAmount"
    />
    <b-container v-show="!preparing">
      <b-row v-if="useStoredCreditCards">
        <b-col>
          <b-form-radio-group
            v-model="selected"
          >
            <div
              v-if="showMyCards"
              id="my-stored-credit-cards"
            >
              <b-tooltip
                target="my-stored-credit-cards"
                placement="lefttop"
                triggers="hover"
              >
                This is the list of Credit Card that are stored against
                <strong>{{ authUserName }}</strong> account within the system.
              </b-tooltip>
              <stored-credit-card-list
                :label="`${authUserName} Stored Cards [ID: ${authUserId}]`"
                :stored-cards="myCards"
              />

              <div class="spacer" />
            </div>

            <div
              v-if="showTxnOwnerCards"
              id="txn-stored-credit-cards"
            >
              <b-tooltip
                target="txn-stored-credit-cards"
                placement="lefttop"
                triggers="hover"
              >
                This is the list of Credit Card that are stored against
                <strong>{{ txnUserName }}</strong> account within the system.
              </b-tooltip>
              <stored-credit-card-list
                :label="`${txnUserName} Stored Cards [ID: ${txnOwerId}]`"
                :stored-cards="txnOwnerCards"
              />
              <div class="spacer" />
            </div>

            <span class="cc-details new-cc">
              <b-form-radio
                id="new-card"
                value="new-card"
              >
                <label for="new-card">
                  Use New Card
                </label>
              </b-form-radio>
            </span>

            <creditCardForm
              v-show="selected == 'new-card'"
              v-model="newCard"
              :payment-testing="paymentTesting"
              :show-card-save="showCardSave"
              @cardValid="cardValidState"
              @hasErrors="setHasdDetailsErrors"
              @override-amount="overrideAmount"
            />
          </b-form-radio-group>
        </b-col>
      </b-row>
      <b-row class="justify-content-center">
        <b-col>
          <payment-button
            v-show="!processing"
            :disabled="disablePayButton"
            :recaptcha="config.recaptcha"
            :tooltip-message="paymentMessage"
            :label="buttonLabel"
            :payment-testing="config.paymentTesting"
            @recaptchaVerified="(v) => recaptchaResponse = v"
            @click="processTransactionPayment"
          />
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { TransactionError, TransactionStatusError } from './errors'
import creditCardForm from './creditCardForm.vue'
import commonMixins from './mixins/common'
import integrapayMixins from './mixins/integrapay'

import storedCreditCardList from './storedcards/stored-credit-card-list.vue'
import transactionError from './modals/transaction-error.vue'
import paymentComplete from './modals/payment-complete.vue'
import paymentFailed from './modals/payment-failed.vue'
import paymentError from './modals/payment-error.vue'
import errorMessage from './modals/error-messages'
import PaymentButton from './payment-button.vue'

const defaultConfig = {
  buttonLabel: 'Pay',
  showPayButton: true,
  useStoredCreditCards: true,
  paymentTesting: false,
  errorHandling: 'internal',
  showCardSave: true,
  cardSaveDefault: false,
  showErrors: true,
  recaptcha: false,
  paymentProcessor: 'integrapay',
  paymentProcessorOptions: {
    integrapay: {}
  }
}

export default {
  name: 'integrapay-card-payment',
  components: {
    'payment-button': PaymentButton,
    creditCardForm,
    transactionError,
    paymentComplete,
    paymentFailed,
    paymentError,
    storedCreditCardList
  },
  mixins: [
    commonMixins,
    integrapayMixins
  ],
  props: {
    txnid: {
      type: [String, Number],
      required: true
    },
    amount: {
      type: Number,
      required: false,
      default: null
    },
    eventListener: {
      type: Function,
      required: false,
      default: null
    },
    inputConfig: {
      type: Object,
      required: false,
      default: () => (defaultConfig)
    }
  },
  data() {
    return {
      selected: 'new-card',
      recaptchaResponse: null,
      override_amount: 0,
      myCards: {},
      txnOwnerCards: {},
      newCard: {
        cardholderName: '',
        cardNumber: '',
        expiryMonth: '',
        expiryYear: '',
        cvc: '',
        cardType: null,
        cardValid: false,
        cardSave: false
      },
      errorDetails: null,
      transctionError: null,
      receiptDetails: null,
      paymentErrors: null
    }
  },
  computed: {
    displayProcessessing: function() {
      return (typeof this.config.displayProcessingSpinner === 'boolean' && this.config.displayProcessingSpinner) || (typeof this.config.displayProcessingSpinner !== 'boolean')
    },
    showRecaptchaButton: function() {
      return (this.config.recaptcha && typeof this.config.recaptcha.key === 'string' && this.config.recaptcha.key.length > 0)
    },
    recaptchaVerified: function() {
      if (this.showRecaptchaButton) {
        return this.recaptchaResponse !== null
      }
      else {
        return true
      }
    },
    enablePayment() {
      return ((this.selected === 'new-card' && this.newCard.cardValid) || this.selected !== 'new-card') && this.state === 'card-selection' && this.config.showPayButton
    },
    preparing() {
      return this.state === 'preparing'
    },
    txn() {
      if (this.paymentProcessor) {
        return this.paymentProcessor.getTxn()
      }
      return ''
    },
    cardselection() {
      return this.state === 'card-selection'
    },
    processing() {
      return this.state === 'processing'
    },
    paymentComplete() {
      return this.state === 'complete'
    },
    paymentFailed() {
      return this.state === 'payment-failed'
    },
    selectedPayer() {
      return (this.selected && this.selected !== 'new-card') ? this.selected : null
    },
    paymentException() {
      return this.state === 'processing-exception'
    },
    showTxnOwnerCards() {
      if (this.paymentProcessor) {
        return (this.config.useStoredCreditCards && Object.keys(this.txnOwnerCards).length > 0 && !this.paymentProcessor.isTxnOwner())
      }
      else {
        return []
      }
    },
    txnOwerId() {
      if (this.paymentProcessor) {
        return this.paymentProcessor.txnOwner()
      }
      else {
        return ''
      }
    },
    authUserId() {
      if (this.paymentProcessor) {
        return this.paymentProcessor.authUser()
      }
      else {
        return ''
      }
    },
    showMyCards() {
      if (this.paymentProcessor) {
        return (this.config.useStoredCreditCards && Object.keys(this.myCards).length > 0)
      }
      else {
        return []
      }
    },
    useStoredCreditCards() {
      if (this.paymentProcessor) {
        return (this.config.useStoredCreditCards && (Object.keys(this.myCards).length > 0 || Object.keys(this.txnOwnerCards).length > 0))
      }
      else {
        return false
      }
    },
    newCardOnly() {
      return !this.useStoredCreditCards
    },

    disablePayButton() {
      if (this.selected === 'new-card' && !this.newCard.cardValid) {
        return true
      }
      return false
    },
    showCardSave() {
      if (this.paymentProcessor) {
        return (this.isLoggedIn && this.config.showCardSave && this.paymentProcessor.isTxnOwner())
      }
      return false
    },
    buttonLabel() {
      return this.config.buttonLabel
    },
    config() {
      // eslint-disable-next-line no-undef
      return { ...defaultConfig, ...this.inputConfig }
    },
    authUserName: function() {
      return this.paymentProcessor.getAuthUserName()
    },
    txnUserName: function() {
      return this.paymentProcessor.getTxnUserName()
    },
    paymentMessage: function() {
      if (this.hasCardDetailsErrors) {
        return 'You need to resolve the errors with the card details.'
      }
      else if (!this.newCard.cardValid) {
        return 'You need to complete the Credit Card Details to Continue.'
      }
      else {
        return 'Complete Payment'
      }
    }
  },
  watch: {
    selectedPayer: function(newValue) {
      if (newValue) {
        this.$emit('valid-card', [true])
      }
      else {
        this.cardValidState(false, true)
      }
    }
  },
  created() {
    this.$on('valid-card', (cb) => {
      if (typeof cb === 'function') {
        cb.call(this, this.newCard.cardValid)
      }
    })

    this.$on('process-payment', (recaptchaResponse, cb) => {
      let receipt = null
      if (this.newCard.cardValid) {
        receipt = this.processTransactionPayment(recaptchaResponse)
      }
      if (typeof cb === 'function') {
        cb.call(this, recaptchaResponse, receipt)
      }
    })
  },
  mounted() {
    this.setState('preparing')
    this.setPaymentTestingCardDetails()
    // Initialize the payment Processor
    switch (this.config.paymentProcessor) {
    case 'integrapay':
    default:
      this.paymentProcessor = this.integrapayClient()
      break
    }

    this.paymentProcessor.initPayment(this.txnid)
      .then(response => {
        if (response.errors) {
          throw new TransactionError(null, response.errors)
        }
        if (response.data.txn === null || !['pending', 'received'].includes(response.data.txn.payment.status)) {
          const paymentData = {
            txn: response.data.txn,
            amount: this.amount,
            payment: response.data.txn.payment
          }
          if (response.data.txn.payment.status === 'completed') {
            throw new TransactionStatusError('Transaction already marked as payment complete.', [{
              category: 'transaction-paid',
              ...paymentData
            }])
          }
          else if (response.data.txn.payment.status === 'refunded') {
            throw new TransactionStatusError('Transaction is refunded.', [{
              category: 'transaction-refunded',
              ...paymentData
            }])
          }
          else if (response.data.txn.payment.status === 'canceled') {
            throw new TransactionStatusError('Transaction is canceled - cannot pay a canceled transaction.', [{
              category: 'transaction-cancelled',
              ...paymentData
            }])
          }
          else if (response.data.txn.payment.status === 'failed') {
            throw new TransactionStatusError('Transaction is canceled - cannot pay a cancelled transaction.', [{
              category: 'transaction-cancelled',
              ...paymentData
            }])
          }
          else {
            throw new TransactionStatusError(`Transaction status invalid for payment: ${response.data.txn.payment.status}`, [{
              category: `transaction-${response.data.txn.payment.status}`,
              ...paymentData
            }])
          }
        }

        if (response.data.account) {
          this.account = response.data.account.id
        }

        // Set the default state of the Checkbox used to Save the Card For future Use.
        if (this.config.showCardSave && this.isLoggedIn) {
          this.newCard.cardSave = this.config.cardSaveDefault
        }
        // Load the Transaction Owners and Logged in user Saved Cards
        if (this.isLoggedIn) {
          return Promise.all([
            this.paymentProcessor.getTxnOwnerStoreCreditCards(),
            this.paymentProcessor.getAuthUserStoreCreditCards()
          ])
            .then(([txnOwnerCards, authUserCards]) => {
              this.$set(this, 'txnOwnerCards', txnOwnerCards)
              this.$set(this, 'myCards', authUserCards)
            })
        }
      })
      .then(_ => {
        this.setState('card-selection')
      })
      .catch(err => {
        const handleError = function(errMessage) {
          if (this.config.errorHandling === 'internal') {
            this.$set(this, 'transctionError', errMessage)
            this.setState('internal-error')
          }
          else {
            this.setState('error')
          }
          this.$emit('error', ['paymentInitialization', errMessage, err.details[0], err.stack, err])
        }

        if (err instanceof TransactionStatusError) {
          handleError.call(this, errorMessage(err.details[0].category))
        }
        else if (err instanceof TransactionError) {
          handleError.call(this, errorMessage(err.details[0].category))
        }
        else if (err.constructor.name === 'GenericError') {
          handleError.call(this, { title: 'Generic Erorr', msg: 'There was an error loading the transaction for payment.' })
        }
        else {
          handleError.call(this, { title: 'Something went wrong' })
        }
      })
  },
  methods: {
    overrideAmount: function(v) {
      try {
        this.override_amount = parseFloat(v)
      }
      catch (err) {
        this.override_amount = null
      }
    },
    setReceiptDetails: function(receipt) {
      this.$set(this, 'receiptDetails', receipt)
    },
    setErrorDetails: function(err) {
      this.$set(this, 'errorDetails', err)
    },
    handlePaymentSuccess: function(receipt) {
      if (receipt.payer) {
        this.$emit('payer-created', [receipt.payer])
      }
      this.$emit('payment-success', [receipt])
      if (this.config.internalSuccessMessage) {
        this.setState('complete')
        this.setReceiptDetails(receipt)
      }
    },
    handlePaymentFailure: function(receipt) {
      this.$emit('payment-failed', [receipt])
      if (this.config.internalFailureMessage) {
        this.setReceiptDetails(receipt)
        this.setState('payment-failed')
      }
    },
    handlePaymentException: function(err) {
      this.$emit('error', ['processTransactionPayment', err])
      this.setState('processing-exception')
      this.setErrorDetails(err)
    },
    processTransactionPayment: function(recaptchaResponse) {
      const reqOptions = {
        additionalOptions: {
          authorization: recaptchaResponse
        },
        amount: this.paymentProcessor.TxnAmount(),
        selectedPayer: this.selectedPayer,
        cardDetails: {
          ...this.newCard,
          cardSave: (this.isLoggedIn) ? this.newCard.cardSave : false
        }
      }
      if (this.$paymentTesting && typeof this.override_amount === 'number' && this.override_amount > 0) {
        reqOptions.override_amount = this.override_amount
      }
      else if (typeof this.amount === 'number' && this.amount > 0 && this.amount <= this.txn.amount) {
        reqOptions.amount = this.amount
        delete reqOptions.override_amount
      }
      else {
        delete reqOptions.amount
      }

      this.setState('processing')
      this.$emit('processing', [{
        txnid: this.paymentProcessor.TxnID(),
        amount: reqOptions.override_amount !== undefined ? reqOptions.override_amount : reqOptions.amount
      }])

      this.paymentProcessor.processPayment(reqOptions)
        .then((receipt) => {
          if (receipt.status === 'success') {
            this.handlePaymentSuccess(receipt)
          }
          else {
            this.handlePaymentFailure(receipt)
          }
          return receipt
        })
        .catch(err => {
          this.handlePaymentException(err)
        })
    }
  }
}
</script>

<style scoped lang="scss">
  .cc-details {
    display: block;
  }
  .spacer {
    min-height: 1.2rem;
  }
</style>
